import React, { useState } from 'react';
import styled from 'styled-components';
import { Main, Text } from 'styles/themeColors';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import Banner from 'components/UI/Banner';
import Button from 'components/UI/Button';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import Icon from 'view/Icon';
import { TEXT_LABELS } from 'constants/order';
import { MANUFACTURING, PACKING } from 'constants/consignmentStates';
import { SALES_ORDER_API_STATUSES, SALES_ORDER_LABELS } from 'constants/salesOrderTypes';
import WarningBanner from 'components/UI/WarningBanner';

const ClickableContainer = styled(FlexboxContainer)`
  cursor: pointer;
`;

interface SalesOrderStatus {
  id: number;
  consignmentId: string;
  salesOrderNumber?: string;
  status: string;
  failureCase?: string;
  requestPayload?: string;
  responsePayload?: string;
  errorMessage?: string;
  httpStatusCode?: number;
  createdDate?: string;
  correlationId?: string;
  actionType?: string;
  salesOrderType?: string;
  // Transformed fields from backend
  transformedMessage?: string;
}

interface Props {
  salesOrderStatus?: SalesOrderStatus;
  consignmentState: string;
  onRetry: () => void;
  disabled?: boolean;
  isSalesOrderApplicable?: boolean;
}

const SalesOrderErrorBanner: React.FC<Props> = ({
  salesOrderStatus,
  consignmentState,
  onRetry,
  disabled = false,
  isSalesOrderApplicable = false
}) => {
  const [showDetails, setShowDetails] = useState(false);

  // Only show banner for PACKING and MANUFACTURING states
  if (![PACKING, MANUFACTURING].includes(consignmentState)) {
    return null;
  }

  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };

  const formatErrorDetails = () => {
    const details: any = {};

    // Add failure case (or GENERIC if not available)
    details.case = salesOrderStatus?.failureCase || 'GENERIC';


    // Add transformed user-friendly message
    if (salesOrderStatus?.transformedMessage) {
      details.message = salesOrderStatus?.transformedMessage;
    } else if (salesOrderStatus?.errorMessage) {
      details.message = salesOrderStatus?.errorMessage;
    }

    return JSON.stringify(details, null, 2);
  };

  const bannerContent = (
    <FlexboxContainer flexDirection="column" width="100%">
      <FlexboxContainer
        flexDirection="row"
        justifyContent="space-between"
        alignItems="flex-start"
        width="100%"
      >
        <FlexboxContainer flexDirection="column" width="calc(100% - 120px)">
          <ClickableContainer
            flexDirection="row"
            alignItems="center"
            gap="8px"
            margin="0 0 8px 0"
            onClick={toggleDetails}
          >
            {showDetails ? (
              <ExpandLess style={{ color: Text.whitePrimary, fontSize: 20 }} />
            ) : (
              <ExpandMore style={{ color: Text.whitePrimary, fontSize: 20 }} />
            )}
            <span style={{ color: Text.whitePrimary, fontSize: '14px' }}>
              {showDetails ? 'Less details' : 'More details'}
            </span>
          </ClickableContainer>

          {showDetails && (
            <FlexboxContainer
              flexDirection="column"
              margin="8px 0 0 0"
              bgColor="rgba(0,0,0,0.1)"
              padding="12px"
              borderRadius="4px"
              border="1px solid rgba(255,255,255,0.2)"
            >
              <pre style={{
                color: Text.whitePrimary,
                fontSize: '12px',
                fontFamily: 'monospace',
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {formatErrorDetails()}
              </pre>
            </FlexboxContainer>
          )}
        </FlexboxContainer>

        <Button
          borderColor={Main.white}
          bgColor={Main.white}
          color={Text.primary}
          onClick={onRetry}
          disabled={disabled}
        >
          <FlexboxContainer flexDirection="row" alignItems="center" gap="6px">
            <Icon name="Refresh" size={22} />
            {TEXT_LABELS.RETRY_BUTTON_TEXT}
          </FlexboxContainer>
        </Button>
      </FlexboxContainer>
    </FlexboxContainer>
  );

  const getContent = () => {
    switch (salesOrderStatus?.status) {
      case SALES_ORDER_API_STATUSES.FAILED:
        return <Banner
          bgColor={Main.error2}
          titleColor={Text.whitePrimary}
          color={Text.whitePrimary}
          title={TEXT_LABELS.SALES_ORDER_ERROR_TITLE}
          text={TEXT_LABELS.SALES_ORDER_ERROR_TEXT}
          children={bannerContent}
        />

      case SALES_ORDER_API_STATUSES.IN_PROGRESS:
        return <WarningBanner
          message={SALES_ORDER_LABELS.SALES_ORDER_IN_PROGRESS}
        />

      case SALES_ORDER_API_STATUSES.SUCCESS:
      case SALES_ORDER_API_STATUSES.COMPLETED:
        return <></>

      default:
        // add condition here to check if sales order is applicable
        return isSalesOrderApplicable ? <WarningBanner
          message={SALES_ORDER_LABELS.SALES_ORDER_IN_PROGRESS}
        /> : <></>
    }
  }

  return getContent();
};

export default SalesOrderErrorBanner;